import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import MainTitle from "../../components/template/MainTitle"
import ButtonWrap from "../../components/template/ButtonWrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faArrowRight, faArrowRotateRight } from "@fortawesome/free-solid-svg-icons"
import DBGenerationStatusPill from "./DBGenerationStatusPill"
import api from "../../components/utils/api"
import TraceStepper from "../../components/template/TraceStepper"

const DBGenarationDetail = () => {
    const [db, setDb] = useState({})
    const [experiment, setExperiment] = useState(null)
    const [traceCode, setTraceCode] = useState(101)
    const params = useParams()
    const navigate = useNavigate();

    const fetchTrace = useCallback(async () => {
        try {
            const res = await api.get(`/api/dbs/${params.id}/traces`)
            setTraceCode(res.data.db_traces.length > 0 ? res.data.db_traces[0].trace_code : 100)
        } catch (err) {
            console.error(err)
        }
    }, [params])

    // Resolve the experiment auto-created during DB generation (always exp_ver 'v0').
    const resolveExperiment = useCallback(async (dbVer) => {
        const res = await api.get(`/api/experiments/resolve?db_ver=${encodeURIComponent(dbVer)}&exp_ver=v0`).catch(() => null)
        setExperiment(res?.data ?? null) // null when not created yet
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const dbRes = await api.get(`/api/dbs/${params.id}`)
                setDb(dbRes.data)
                resolveExperiment(dbRes.data.db_ver)
            } catch (err) {
                console.error(err)
            }
        }
        fetchData()
        fetchTrace()
    }, [params, fetchTrace, resolveExperiment])

    // The v0 experiment is created mid-generation, so a refresh during progress
    // should reveal the link by re-resolving it when not yet found.
    const handleRefresh = () => {
        fetchTrace()
        if (!experiment && db.db_ver) resolveExperiment(db.db_ver)
    }


    const defaultSteps = useMemo(() => [
        {
            id: 0, title: 'CochlDB generation', stepStatus: 'waiting', subSteps: [
                { id: 1, message: 'CochlDB generation started', subStepStatus: 'waiting' },
                { id: 2, message: 'Create new Cochlogy', subStepStatus: 'waiting' },
                { id: 3, message: 'Verify data split', subStepStatus: 'waiting' },
                { id: 4, message: 'Get raw data', subStepStatus: 'waiting' },
                { id: 5, message: 'Create new CochlDB', subStepStatus: 'waiting' },
                { id: 6, message: 'Create new class filter and benchmark merge rule', subStepStatus: 'waiting' },
                { id: 9, message: 'CochlDB generation completed', subStepStatus: 'waiting' },
            ]
        },
        {
            id: 1, title: 'Model training', stepStatus: 'waiting', subSteps: [
                { id: 1, message: 'Model training started', subStepStatus: 'waiting' },
                {
                    id: 2, message: 'Prepare model training', subStepStatus: 'waiting',
                    action: experiment?.id ? (
                        <a
                            href={`/experiments/${experiment.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Go to training experiment (${experiment.status.name})`}
                            className="ml-2 flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-600"
                        >Show progress <FontAwesomeIcon icon={faArrowRight} /></a>
                    ) : null
                },
                { id: 3, message: 'Download data', subStepStatus: 'waiting' },
                {
                    id: 4, message: 'Train model', subStepStatus: 'waiting',
                    action: experiment?.id ? (
                        <a
                            href={`/training-progress/dbs/${params.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Show live training progress"
                            className="ml-2 flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-600"
                        >Show progress <FontAwesomeIcon icon={faArrowRight} /></a>
                    ) : null
                },
                { id: 5, message: 'Upload model performance score', subStepStatus: 'waiting' },
                { id: 9, message: 'Model training completed', subStepStatus: 'waiting' },
            ]
        },
        {
            id: 2, title: 'CochlDB analysis: histogram', stepStatus: 'waiting', subSteps: [
                { id: 1, message: 'Histogram analysis started', subStepStatus: 'waiting' },
                { id: 2, message: 'Download resources', subStepStatus: 'waiting' },
                { id: 3, message: 'Analyze train data', subStepStatus: 'waiting' },
                { id: 4, message: 'Analyze valid data', subStepStatus: 'waiting' },
                { id: 5, message: 'Analyze internal test data', subStepStatus: 'waiting' },
                { id: 6, message: 'Analyze external test data', subStepStatus: 'waiting' },
                { id: 7, message: 'Calculate histogram score', subStepStatus: 'waiting' },
                { id: 9, message: 'Histogram analysis completed', subStepStatus: 'waiting' },
            ]
        },
        {
            id: 3, title: 'CochlDB analysis: FP statistics', stepStatus: 'waiting', subSteps: [
                { id: 1, message: 'FP analysis started', subStepStatus: 'waiting' },
                { id: 2, message: 'Compute FP statistics', subStepStatus: 'waiting' },
                { id: 3, message: 'Compute FP health score', subStepStatus: 'waiting' },
                { id: 4, message: 'Aggregate prediction class counts', subStepStatus: 'waiting' },
                { id: 9, message: 'Analysis completed', subStepStatus: 'waiting' },
            ]
        },
    ], [experiment, params.id])

    return (
        <div className='default-background'>
            {db.hasOwnProperty('db_ver') && <div>
                <div className="flex justify-between items-center my-5">
                    <div className="flex items-center">
                        <MainTitle title={db.db_ver} />
                        <div className="mx-3"><DBGenerationStatusPill value={db.status.name} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-center">
                        {db.status.name !== 'GENERATED' && <ButtonWrap contents={<FontAwesomeIcon icon={faArrowRotateRight} />} onClick={handleRefresh} />}
                        <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/db-generation')} />
                    </div>
                </div>
                <TraceStepper defaultSteps={defaultSteps} traceCode={db.status.value === 10 ? 199 : traceCode} />
            </div>}
        </div>
    )
}

export default DBGenarationDetail